import { useState } from 'react'
import { MoonStar, Pin } from 'lucide-react'
import { Shell, useShellMeta } from '../../../admin/Shell.js'
import { SegmentedControl } from '../../../admin/pickers.js'
import { Link } from '../../../router.js'
import { BandeauLecture, Pastille, fmtNombre, useVueSonde, type Ton } from '../commun.js'
import {
  LIBELLE_ORIGINE, LIBELLE_TYPE, dateCourte, dateNuit, duree, estListeNuits, heureParis, jourSuivant, tonNuit, totalTrous,
  type ListeNuits, type Nuit,
} from './nuits-model.js'
import './nuits.css'

/**
 * TECHNIQUE › INFRASTRUCTURE › NUITS (`/infrastructure/nuits`, 21/10/2026).
 *
 * Les nuits commerciales du club (08:00 → 08:00, heure de Paris), lues dans
 * Sonde (`GET /v1/nights`) par le relais de Gestion. Une mosaïque des trente
 * dernières nuits en couleurs (verdict du rapport du matin, à défaut les
 * comptes de Sonde), puis une ligne par nuit : incidents, alertes, trous de
 * supervision, épinglage. Chaque nuit s'ouvre sur son rapport et son rejeu.
 *
 * Honnêteté : une nuit sans comptes est grise, jamais verte ; une nuit
 * fermée et calme est « éteinte comme prévu », pas « verte » ; une nuit que
 * Sonde n'a pas résumée reste ouvrable (Sonde la calcule à la demande).
 */

type Filtre = 'toutes' | 'soirees' | 'epinglees'

const FILTRES: { value: Filtre; label: string }[] = [
  { value: 'toutes', label: 'Toutes' }, { value: 'soirees', label: 'Soirées' }, { value: 'epinglees', label: 'Épinglées' },
]

const CHEMIN: Record<Filtre, string> = {
  toutes: 'nights?limit=60',
  soirees: 'nights?operating=1&limit=60',
  epinglees: 'nights?pinned=1&limit=60',
}

const LEGENDE: { ton: Ton; libelle: string }[] = [
  { ton: 'ok', libelle: 'Sans incident' },
  { ton: 'warn', libelle: 'À voir' },
  { ton: 'err', libelle: 'Problème ou incident critique' },
  { ton: 'off', libelle: 'Club fermé, calme' },
  { ton: 'gris', libelle: 'Inconnu ou en cours' },
]

/** Une nuit au format de la liste : la ligne cliquable. */
function LigneNuit({ n }: { n: Nuit }) {
  const t = tonNuit(n)
  const c = n.counts
  const trous = totalTrous(n.sources_unavailable)
  const horaires = n.opening_at || n.closing_at ? `${heureParis(n.opening_at)} → ${heureParis(n.closing_at)}` : null
  return (
    <li>
      <Link className={`sv-nuit-ligne sv-ton--${t.ton}`} href={`/infrastructure/nuits/${n.night_date}`}>
        <span className="sv-nuit-ligne__haut">
          <span className="sv-nuit-ligne__date">{dateNuit(n.night_date)}</span>
          {n.kind ? <span className="sv-nuit-neutre">{LIBELLE_TYPE[n.kind] ?? n.kind}</span> : null}
          {n.status === 'open' ? <Pastille ton="gris">En cours</Pastille> : null}
          {n.pinned ? <span className="sv-nuit-neutre" title={n.pinned.reason ?? undefined}><Pin size={13} aria-hidden="true" /> Épinglée</span> : null}
          <Pastille ton={t.ton} title={t.source === 'rapport' ? 'Verdict du rapport du matin' : t.source === 'comptes' ? 'D’après les comptes de Sonde (pas encore de rapport)' : 'Sonde n’a pas de comptes pour cette nuit'}>
            {t.libelle}
          </Pastille>
        </span>
        <span className="sv-nuit-ligne__meta">
          {n.label ? `${n.label} · ` : ''}
          {horaires ? `Ouverture ${horaires}` : 'Pas d’horaires d’ouverture'}
          {n.origin ? ` · ${LIBELLE_ORIGINE[n.origin] ?? n.origin}` : ''}
        </span>
        <ul className="sv-nuit-chiffres" aria-label="Chiffres de la nuit">
          <li>Incidents <strong>{fmtNombre(c?.incidents)}</strong>{c && c.incidents_critical > 0 ? ` dont ${c.incidents_critical} critique${c.incidents_critical > 1 ? 's' : ''}` : ''}</li>
          <li>Alertes <strong>{fmtNombre(c?.alerts)}</strong>{c && c.alerts_shadow > 0 ? ` (${c.alerts_shadow} en ombre)` : ''}</li>
          <li>Passages hors ligne <strong>{fmtNombre(c?.offline_transitions)}</strong></li>
          <li className={trous.nombre > 0 ? undefined : 'sv-nuit-gris'}>
            Trous de supervision <strong>{trous.nombre}</strong>{trous.nombre > 0 ? ` (${duree(trous.secondes)})` : ''}
          </li>
        </ul>
        <span className="sv-nuit-ligne__meta">
          {n.report ? `Rapport : ${n.report.subject}` : 'Rapport du matin pas encore préparé'}
          {n.computed_at ? ` · résumé de Sonde à ${heureParis(n.computed_at)}` : ''}
        </span>
      </Link>
    </li>
  )
}

/** Mosaïque : une case par date de la période, même sans nuit enregistrée. */
function Mosaique({ data }: { data: ListeNuits }) {
  const par = new Map(data.nights.map((n) => [n.night_date, n]))
  const dates: string[] = []
  for (let d = data.from, i = 0; d <= data.to && i < 62; d = jourSuivant(d), i++) dates.push(d)
  dates.reverse()
  return (
    <>
      <div className="sv-nuit-mosaique" role="list" aria-label="Nuits de la période">
        {dates.map((d) => {
          const n = par.get(d)
          const t = n ? tonNuit(n) : null
          const c = dateCourte(d)
          return (
            <Link key={d} role="listitem" href={`/infrastructure/nuits/${d}`}
                  className={`sv-nuit-case${n ? ` sv-ton--${t!.ton}` : ' sv-nuit-case--absente'}`}
                  title={`${dateNuit(d, true)} — ${t ? t.libelle : 'pas de résumé enregistré'}`}>
              <span className="sv-nuit-case__jour">{c.jour}</span>
              <span className="sv-nuit-case__num">{c.num}</span>
            </Link>
          )
        })}
      </div>
      <ul className="sv-nuit-legende" aria-label="Légende">
        {LEGENDE.map((l) => <li key={l.ton} className={`sv-ton--${l.ton}`}><i aria-hidden="true" />{l.libelle}</li>)}
        <li><i aria-hidden="true" className="sv-nuit-legende__vide" />Pas de résumé</li>
      </ul>
    </>
  )
}

function ListeVue({ filtre }: { filtre: Filtre }) {
  const { lecture, precedente } = useVueSonde(CHEMIN[filtre], estListeNuits, 60_000)
  const data = lecture?.etat === 'ok' ? lecture.data : precedente
  const nuits = data ? [...data.nights].sort((a, b) => b.night_date.localeCompare(a.night_date)) : null
  return (
    <>
      <BandeauLecture lecture={lecture} />
      {filtre === 'toutes' && data ? (
        <section className="lc-panel sv-nuit-section" aria-labelledby="sv-nuit-mos">
          <header className="lc-panel__head">
            <h2 className="lc-section-title" id="sv-nuit-mos">Les nuits, du {dateNuit(data.from)} au {dateNuit(data.to)}</h2>
            <span className="lc-panel__spacer" />
            <span className="lc-hint">{data.pinned_count} / {data.pinned_max} épinglées</span>
          </header>
          <div className="lc-panel__body"><Mosaique data={data} /></div>
        </section>
      ) : null}
      <section className="lc-panel sv-nuit-section" aria-labelledby="sv-nuit-lst">
        <header className="lc-panel__head">
          <h2 className="lc-section-title" id="sv-nuit-lst">{filtre === 'epinglees' ? 'Nuits épinglées' : filtre === 'soirees' ? 'Soirées' : 'Nuits résumées par Sonde'}</h2>
          <span className="lc-panel__spacer" />
          <span className="lc-hint">{nuits ? `${nuits.length} nuit${nuits.length > 1 ? 's' : ''}` : '—'}</span>
        </header>
        <div className="lc-panel__body">
          {!data && !lecture ? <p className="sv-nuit-vide">Lecture de Sonde…</p> : null}
          {nuits && nuits.length === 0 ? (
            <p className="sv-nuit-vide">
              {filtre === 'epinglees'
                ? 'Aucune nuit épinglée. Épingler une nuit garde son détail complet au-delà de la rétention normale.'
                : 'Aucune nuit résumée sur cette période. Sonde résume chaque nuit à sa clôture (8 h) ; une nuit sans résumé s’ouvre quand même, Sonde la calcule à la demande.'}
            </p>
          ) : null}
          {nuits && nuits.length > 0 ? <ul className="sv-nuit-liste">{nuits.map((n) => <LigneNuit key={n.night_date} n={n} />)}</ul> : null}
        </div>
      </section>
    </>
  )
}

export function NuitsPage() {
  const meta = useShellMeta()
  const [filtre, setFiltre] = useState<Filtre>('toutes')
  return (
    <Shell active="infrastructure" meta={meta}>
      <main className="lc-page lc-stagger lc-page--large sv-nuit">
        <Link className="lc-crumb" href="/infrastructure">← Infrastructure</Link>
        <header className="sv-nuit-tete">
          <div>
            <h1 className="lc-title"><MoonStar size={24} aria-hidden="true" /> Nuits</h1>
            <p className="lc-subtitle">Chaque nuit du club, de 8 h à 8 h : ce qui s’est passé, le rapport du matin et le rejeu minute par minute.</p>
          </div>
          <SegmentedControl value={filtre} options={FILTRES} onChange={setFiltre} ariaLabel="Nuits à montrer" />
        </header>
        {/* La clé remonte la lecture : jamais la liste d'un filtre sous le titre d'un autre. */}
        <ListeVue key={filtre} filtre={filtre} />
      </main>
    </Shell>
  )
}
